import { useEffect, useState } from "react";

import * as api from "../lib/api";
import { SessionExpired } from "../lib/api";
import type { AppConfig } from "../lib/config";
import type { Identity } from "../lib/auth";
import type { StudentDetail as Detail } from "../lib/types";
import StudentDetail from "./StudentDetail";

interface Props {
  config: AppConfig;
  identity: Identity;
  /** A student id for staff, or null to load the caller's own record (/me). */
  studentId: string | null;
  refreshKey: number;
  onSessionExpired: () => void;
}

export default function DetailView({
  config,
  identity,
  studentId,
  refreshKey,
  onSessionExpired,
}: Props) {
  const [detail, setDetail] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const request = studentId
      ? api.getStudent(config, identity, studentId)
      : api.me(config, identity);
    request
      .then((result) => !cancelled && setDetail(result))
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof SessionExpired) onSessionExpired();
        else setError(err instanceof Error ? err.message : "Could not load the record.");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [config, identity, studentId, refreshKey, onSessionExpired]);

  if (loading && !detail) return <p className="text-sm text-slate-500">Loading record…</p>;
  if (error)
    return (
      <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
        {error}
      </p>
    );
  if (!detail) return null;
  return <StudentDetail detail={detail} />;
}
